<template>
  <Card :title="cardTitle">
    <!-- 一般商品顯示固定金額;儲值選物(-3)在輸入階段改成金額輸入,付款中則顯示已輸入金額 -->
    <OrderSummary v-if="!showAmountInput || awaitingExternal" :amount="session.amount" />

    <div v-else class="amount-input">
      <div class="amount-input-label">自行輸入金額</div>
      <div class="amount-field" :class="{ invalid: amountText !== '' && !amountValid }">
        <span class="amount-prefix">NT$</span>
        <input
          v-model="amountText"
          type="number"
          inputmode="numeric"
          min="1"
          step="1"
          placeholder="請輸入金額"
          :disabled="paying"
        />
        <button
          v-if="amountText !== ''"
          type="button"
          class="amount-clear"
          :disabled="paying"
          aria-label="清除金額"
          @click="amountText = ''"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-linecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke-width="1.6" />
            <path d="M9 9l6 6M15 9l-6 6" stroke-width="2" />
          </svg>
        </button>
      </div>
      <p v-if="amountText !== '' && !amountValid" class="amount-hint">請輸入大於 0 的整數金額</p>

      <div class="quick-label">或點選常用金額</div>
      <div class="quick-picks">
        <button
          v-for="q in quickAmounts"
          :key="q"
          type="button"
          class="quick-pick"
          :class="{ active: amount === q }"
          :disabled="paying"
          @click="amountText = String(q)"
        >
          {{ q }}
        </button>
      </div>
    </div>

    <div v-if="showCountdown" class="countdown" :class="{ warn: remaining <= 10 }">
      <div class="countdown-label">剩餘時間</div>
      <div class="countdown-value">
        <template v-if="remaining > 0">{{ remaining }} 秒</template>
        <template v-else>已逾時,等待系統確認…</template>
      </div>
      <div class="countdown-bar">
        <div class="countdown-bar-fill" :style="{ width: percent + '%' }" />
      </div>
    </div>

    <div v-if="!awaitingExternal" class="channels">
      <button
        v-for="opt in channels"
        :key="opt.value"
        class="channel"
        :class="{ active: selected === opt.value }"
        :disabled="paying"
        @click="selected = opt.value"
      >
        <span class="channel-radio" :class="{ checked: selected === opt.value }" />
        <span class="channel-logo">
          <component :is="opt.logo" />
        </span>
      </button>
    </div>

    <div v-else class="pending">
      <div v-if="pickedLogo" class="pending-logo">
        <component :is="pickedLogo" />
      </div>
      <div class="pending-hint">
        <Spinner />
        <span>付款進行中…請於新開的視窗完成付款</span>
      </div>
    </div>

    <p v-if="!awaitingExternal && error" class="err">{{ error }}</p>

    <template #footer>
      <template v-if="!awaitingExternal">
        <button
          class="btn btn-ghost"
          :disabled="paying || cancelling"
          @click="$emit('cancel')"
        >
          取消
        </button>
        <button
          class="btn btn-primary"
          :disabled="!canPay || paying"
          @click="onConfirm"
        >
          <Spinner v-if="paying" />
          <span v-else>確認付款</span>
        </button>
      </template>

      <button
        v-else
        class="btn btn-ghost full"
        :disabled="cancelling"
        @click="$emit('cancel')"
      >
        取消交易
      </button>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, markRaw, type Component } from 'vue'
import Card from './Card.vue'
import OrderSummary from './OrderSummary.vue'
import Spinner from './Spinner.vue'
import LinePayLogo from './icons/LinePayLogo.vue'
import JkoPayLogo from './icons/JkoPayLogo.vue'
import {
  DEFAULT_COMMON_AMOUNTS,
  QrSessionState,
  WAREHOUSE_STORED_VALUE,
  type PayChannel,
  type QrSessionVo,
} from '@/types/qr'

/** 對應 server MPAY_INPUT_TIMEOUT:選電子後到建單付款的 60 秒窗口(仍在 WAITING_NOTIFY) */
const WAIT_PAY_TIMEOUT_SECONDS = 60

const props = defineProps<{
  session: QrSessionVo
  /** 儲值選物常用金額快選(createSession 回傳,由父層持久化後傳入);空 → 用預設 */
  commonAmounts?: number[] | null
  /** 該機台可用支付通路(PayChannel value);空 → fallback 顯示全部已支援通路 */
  mpayList?: string[] | null
  /** 新流程:選電子支付後即使還沒 -3 訂單也一律顯示金額輸入(手機端建單) */
  requireAmount?: boolean
  paying?: boolean
  cancelling?: boolean
  awaitingExternal?: boolean
  /** 進入 WAITING_PAY 的 epoch ms,用來算 50 秒選支付倒數 */
  waitPayStartedAt: number
  error?: string
}>()

/** 後端 machine 有設定就用它,否則 fallback 預設 [20,25,30,35] */
const quickAmounts = computed<readonly number[]>(() =>
  props.commonAmounts && props.commonAmounts.length
    ? props.commonAmounts
    : DEFAULT_COMMON_AMOUNTS,
)
const emit = defineEmits<{
  (e: 'pay', channel: PayChannel, amount?: number): void
  (e: 'cancel'): void
}>()

/** 前端能渲染的所有支付通路(logo 等) */
const ALL_CHANNELS: Array<{ value: PayChannel; logo: Component }> = [
  { value: 'line-pay', logo: markRaw(LinePayLogo) },
  { value: 'jko-pay', logo: markRaw(JkoPayLogo) },
]

/** 依機台 mpayList 過濾;未帶 / 空 → fallback 顯示全部(維持舊行為) */
const channels = computed(() => {
  const allow = props.mpayList
  if (!allow || !allow.length) return ALL_CHANNELS
  return ALL_CHANNELS.filter((opt) => allow.includes(opt.value))
})

const selected = ref<PayChannel | null>(null)

/** 儲值選物(-3):amount 由手機輸入,session.amount 只是機台送的佔位值,不能當售價顯示 */
const isStoredValue = computed(
  () => String(props.session.warehouseNumber) === WAREHOUSE_STORED_VALUE,
)

/** 需要手機輸入金額:新流程選電子支付(requireAmount),或舊流程 -3 儲值選物 */
const showAmountInput = computed(() => !!props.requireAmount || isStoredValue.value)

const cardTitle = computed(() => {
  if (props.awaitingExternal) return ''
  return showAmountInput.value ? '輸入付款金額' : '請選擇支付方式'
})

/**
 * 只有「選電子後、尚未建單付款」這段(仍在 MACHINE_READY)server 才有逾時窗口。
 * 進 WAITING_PAY / PAYING 後 server 不再判 EXPIRED,此時再顯示倒數會永遠停在
 * 「已逾時,等待系統確認…」誤導使用者 → 不顯示。
 */
const showCountdown = computed(
  () => !props.awaitingExternal && props.session.state === QrSessionState.MACHINE_READY,
)

const amountText = ref('')
/** 解析為正整數才有效,否則為 null */
const amount = computed<number | null>(() => {
  const n = Number(amountText.value)
  return Number.isInteger(n) && n > 0 ? n : null
})
const amountValid = computed(() => amount.value !== null)

/** 一般商品只需選通路;需輸入金額時還要有效金額 */
const canPay = computed(
  () => !!selected.value && (!showAmountInput.value || amountValid.value),
)

function onConfirm() {
  if (!canPay.value || props.paying) return
  emit('pay', selected.value!, showAmountInput.value ? amount.value! : undefined)
}

const pickedLogo = computed<Component | null>(() => {
  const c = props.session.payChannel
  if (!c) return null
  return ALL_CHANNELS.find((opt) => opt.value === c)?.logo ?? null
})

const now = ref(Date.now())
let tickTimer: number | null = null

const remaining = computed(() => {
  const elapsed = Math.floor((now.value - props.waitPayStartedAt) / 1000)
  return Math.max(0, WAIT_PAY_TIMEOUT_SECONDS - elapsed)
})
const percent = computed(() => (remaining.value / WAIT_PAY_TIMEOUT_SECONDS) * 100)

function startTimer() {
  stopTimer()
  now.value = Date.now()
  tickTimer = window.setInterval(() => {
    now.value = Date.now()
  }, 1000)
}
function stopTimer() {
  if (tickTimer != null) {
    window.clearInterval(tickTimer)
    tickTimer = null
  }
}

watch(() => props.waitPayStartedAt, startTimer)
onMounted(startTimer)
onBeforeUnmount(stopTimer)
</script>

<style scoped>
.channels {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 16px 0 4px;
}
.channel {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.channel.active {
  border-color: #2563eb;
  box-shadow: 0 2px 12px rgba(37, 99, 235, 0.18);
}
.channel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.channel-radio {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  position: relative;
  transition: border-color 0.15s;
}
.channel-radio.checked {
  border-color: #2563eb;
}
.channel-radio.checked::after {
  content: '';
  position: absolute;
  inset: 3px;
  background: #2563eb;
  border-radius: 50%;
}
.channel-logo {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.err {
  color: #dc2626;
  font-size: 13px;
  margin: 8px 0 0;
}
.amount-input {
  background: linear-gradient(135deg, #fff7ed, #ffedd5);
  border-radius: 12px;
  padding: 16px;
}
.amount-input-label {
  font-size: 13px;
  color: #9a3412;
  margin-bottom: 10px;
}
.amount-field {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 10px 14px;
}
.amount-field.invalid {
  border-color: #dc2626;
}
.amount-prefix {
  color: #9a3412;
  font-size: 18px;
  font-weight: 600;
}
.amount-field input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #ea580c;
  font-size: 26px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  -moz-appearance: textfield;
}
.amount-field input::-webkit-outer-spin-button,
.amount-field input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.amount-clear {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}
.amount-clear:active {
  color: #6b7280;
}
.amount-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.amount-clear svg {
  width: 20px;
  height: 20px;
}
.quick-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #9a3412;
  margin: 16px 0 10px;
}
.quick-label::before,
.quick-label::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #fed7aa;
}
.quick-picks {
  display: flex;
  gap: 8px;
}
.quick-pick {
  flex: 1;
  padding: 10px 0;
  background: #fff;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #9a3412;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.quick-pick.active {
  background: #ea580c;
  border-color: #ea580c;
  color: #fff;
}
.quick-pick:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.amount-hint {
  color: #dc2626;
  font-size: 12px;
  margin: 10px 0 0;
}
.pending {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  padding: 20px 16px;
  background: #eff6ff;
  color: #1e40af;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
}
.pending-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}
.pending-hint {
  display: flex;
  align-items: center;
  gap: 10px;
}
.full {
  width: 100%;
}
.countdown {
  background: #fff7ed;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 16px 0 4px;
}
.countdown-label {
  font-size: 12px;
  color: #9a3412;
}
.countdown-value {
  font-size: 22px;
  font-weight: 700;
  color: #ea580c;
  margin: 2px 0 8px;
  font-variant-numeric: tabular-nums;
}
.countdown-bar {
  height: 6px;
  background: #fed7aa;
  border-radius: 999px;
  overflow: hidden;
}
.countdown-bar-fill {
  height: 100%;
  background: #ea580c;
  border-radius: 999px;
  transition: width 1s linear;
}
.countdown.warn .countdown-value {
  color: #b91c1c;
}
.countdown.warn .countdown-bar-fill {
  background: #b91c1c;
}
</style>
